import React from "react";
import { type StaticImageData } from "next/image";
import { Box, type SpaceProps } from "@chakra-ui/react";
import { trackDemoButtonClicked } from "components/event-tracking";
import { type Theme } from "contexts/color-theme";
import { RuleAndTextCta } from "ui/text";
import { useDemoLinkWithParameters } from "hooks/use-demo-link";
import { DirectionalButton } from "ui/button";
import { ProductShowcase, ProductShowcaseImage, type ProductShowcaseProps } from "ui/product-showcase";
import defaultProductImage from "../../../public/assets/demo-embed/audiences.png";
import defaultMobileProductImage from "../../../public/assets/demo-embed/audiences-mobile.png";

type ProductDemoProps = {
  /**
   * Determines the text color.
   * @default "light"
   */
  theme?: Theme;

  /**
   * Variant of the product showcase image.
   * @default "sunset"
   */
  showcaseImageVariant?: ProductShowcaseProps["variant"];

  /**
   * Text to display on mobile screens when the demo link is used.
   * @default "Get a personalized demo to learn how you can sync customer data to any business tool."
   */
  demoLinkText?: string;

  /**
   * Override the product image to display on desktop screens.
   */
  productImage?: StaticImageData;

  /**
   * Override the product image alt text on desktop screens.
   */
  productImageAlt?: string;

  /**
   * Override the product image to display on mobile screens.
   */
  mobileProductImage?: StaticImageData;

  /**
   * Override the product image alt text on mobile screens.
   */
  mobileProductImageAlt?: string;
} & SpaceProps;

export function ProductDemo({
  theme = "light",
  showcaseImageVariant = "sunset",
  demoLinkText = "Get a personalized demo to learn how you can sync customer data to any business tool.",
  productImage = defaultProductImage,
  mobileProductImage = defaultMobileProductImage,
  productImageAlt = "Hightouch Audiences user interface.",
  mobileProductImageAlt = "Hightouch Audiences user interface.",
  ...spaceProps
}: ProductDemoProps) {
  const demoLink = useDemoLinkWithParameters();

  return (
    <Box {...spaceProps}>
      <ProductShowcase
        variant={showcaseImageVariant}
        productScreen={<ProductShowcaseImage src={productImage} alt={productImageAlt} />}
        mobileProductScreen={<ProductShowcaseImage isMobile src={mobileProductImage} alt={mobileProductImageAlt} />}
      />
      <RuleAndTextCta
        hasRuleHidden
        theme={theme}
        mt={{ base: "small", md: "big" }}
        action={
          <DirectionalButton href={demoLink} onClick={trackDemoButtonClicked}>
            Get a demo
          </DirectionalButton>
        }
      >
        {demoLinkText}
      </RuleAndTextCta>
    </Box>
  );
}
